import type { UpdateTourStatus } from "@home-finance-manager/shared/types/tour"
import { LoginUserResponse, RegisterUserResponse } from "@home-finance-manager/shared/types/users"
import { BaseSDK } from "./base-sdk"

export class UsersSDK extends BaseSDK {
  async login(email: string, password: string): Promise<LoginUserResponse> {
    return this.fetchWithErrorHandling<LoginUserResponse>(`${this.baseUrl}/users/sessions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    })
  }

  async register(
    email: string,
    password: string,
    invitationToken?: string
  ): Promise<RegisterUserResponse> {
    const url = new URL(`${this.baseUrl}/users`)
    if (invitationToken) {
      url.searchParams.set("token", invitationToken)
    }

    return this.fetchWithErrorHandling<RegisterUserResponse>(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    })
  }

  async updateTourStatus(data: UpdateTourStatus): Promise<{
    data: {
      tourStartedAt: string | null
      tourCompletedAt: string | null
      tourStepIndex: number | null
    }
  }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/users/tour-status`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    })
  }

  async getSettings(): Promise<{
    data: {
      id: number
      userId: number
      locale: string
      preferences: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }
  }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/users/settings`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    })
  }

  async updateLocale(locale: string): Promise<{
    data: {
      id: number
      userId: number
      locale: string
      preferences: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }
  }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/users/settings/locale`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ locale }),
    })
  }

  async updatePreferences(preferences: Record<string, unknown>): Promise<{
    data: {
      id: number
      userId: number
      locale: string
      preferences: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }
  }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/users/settings/preferences`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ preferences }),
    })
  }

  async updateCurrency(currency: string): Promise<{
    data: {
      id: number
      userId: number
      locale: string
      preferences: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }
  }> {
    return this.updatePreferences({ currency })
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/users/verify-email/${token}`,
      {
        method: "GET",
      }
    )
  }

  async resendVerificationEmail(): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/users/resend-verification`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
      }
    )
  }

  async resendVerificationEmailByEmail(email: string): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/users/resend-verification-by-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }
    )
  }

  async refreshToken(): Promise<LoginUserResponse> {
    return this.fetchWithErrorHandling<LoginUserResponse>(
      `${this.baseUrl}/users/sessions/refresh`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
      }
    )
  }
}
